/**
 * BILLING MODE — ONE BILL, OR SEPARATE BILLS.
 *
 * One toggle, on the same screen as the household's other settings. The order
 * never splits: one pick list, one delivery, one delivery fee. Only the billing
 * changes, and the grocer does the billing — there is no price, total or
 * payment anywhere in here.
 *
 * Only the owner or an adult can change it. Everyone else sees the setting and
 * who the bills would go to, and cannot move it.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Home, Mail, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '../ui/input';

const Row = ({ group, t }) => (
    <li className="flex flex-wrap items-baseline gap-x-2 gap-y-1 py-1.5"
        data-testid={`grocery-billing-group-${group.member_id || 'shared'}`}>
        {group.shared ? <Home className="h-4 w-4 shrink-0 text-muted-foreground" /> : null}
        <span className="font-bold text-foreground">
            {group.shared ? t('grocery.billShared') : group.name}
        </span>
        <span className="text-sm text-muted-foreground">
            {t('grocery.billItems', { count: group.item_count })}
        </span>
        {group.routed_to_owner ? (
            <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-300"
                data-testid={`grocery-billing-missing-${group.member_id}`}>
                <AlertTriangle className="h-3.5 w-3.5" />
                {t('grocery.billMissingContact', { owner: group.routed_to_name })}
            </span>
        ) : null}
    </li>
);

export const BillingPanel = ({ api, session, lang, billing, members, meId, onChanged,
    frost = '' }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [edit, setEdit] = useState(null);       // { id, value }
    if (!billing) return null;

    const mode = billing.billing_mode;
    // THE PERSON IN THE "WHO'S ADDING" CHIP is the one acting, so the toggle
    // locks and unlocks with the chip — the owner and any adult may move it.
    const me = (members || []).find((m) => m.id === meId);
    const may = me ? ['owner', 'adult'].includes(me.role) : !!billing.may_change;
    const people = (billing.billing_groups || []).filter((g) => !g.shared);

    const setMode = async (next) => {
        if (next === mode || !may) return;
        setBusy(true);
        try {
            await api(`/household/billing${meId ? `?member_id=${meId}` : ''}`, {
                method: 'PATCH', session, lang, body: { billing_mode: next },
            });
            onChanged();
        } catch (e) {
            toast.error(e.message === 'owner_or_adult_only'
                ? t('grocery.billNotAllowed') : t('grocery.billSaveFailed'));
        } finally { setBusy(false); }
    };

    const saveContact = async (id, value) => {
        setBusy(true);
        try {
            await api(`/members/${id}/bill-contact${meId ? `?member_id=${meId}` : ''}`, {
                method: 'PATCH', session, lang,
                body: value.includes('@') ? { bill_email: value } : { bill_phone: value },
            });
            setEdit(null);
            onChanged();
        } catch (e) {
            toast.error(e.message === 'bad_email' ? t('grocery.billBadEmail')
                : t('grocery.billSaveFailed'));
        } finally { setBusy(false); }
    };

    return (
        <section className={`rounded-2xl border-2 border-input bg-card p-4${frost}`} data-testid="grocery-billing"
            data-launcher-avoid="lift">
            <button onClick={() => setOpen(!open)} data-testid="grocery-billing-toggle-open"
                className="flex min-h-[44px] w-full items-center justify-between gap-2 text-left">
                <span className="flex items-center gap-2 font-bold text-foreground">
                    <Receipt className="h-5 w-5" />{t('grocery.billTitle')}
                </span>
                <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-300"
                    data-testid="grocery-billing-current">
                    {mode === 'separate_bills' ? t('grocery.billSeparate') : t('grocery.billOne')}
                </span>
            </button>

            {open ? (
                <div className="mt-3 space-y-3">
                    <div className="flex flex-wrap gap-2">
                        {['one_bill', 'separate_bills'].map((m) => (
                            <button key={m} onClick={() => setMode(m)} disabled={busy || !may}
                                data-testid={`grocery-billing-${m}`}
                                aria-pressed={mode === m}
                                className={`min-h-[44px] rounded-full border-2 px-4 text-sm font-semibold disabled:opacity-60 ${mode === m
                                    ? 'border-emerald-600 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-100'
                                    : 'border-slate-300 text-foreground dark:border-slate-600'}`}>
                                {m === 'one_bill' ? t('grocery.billOne') : t('grocery.billSeparate')}
                            </button>
                        ))}
                    </div>
                    {!may ? (
                        <p className="text-xs font-semibold text-muted-foreground"
                            data-testid="grocery-billing-locked">{t('grocery.billNotAllowed')}</p>
                    ) : null}

                    <p className="text-sm text-muted-foreground" data-testid="grocery-billing-summary">
                        {mode === 'separate_bills'
                            ? t('grocery.billSummarySeparate', {
                                names: people.map((p) => p.name).join(', ')
                                    || t('grocery.billShared'),
                            })
                            : t('grocery.billSummaryOne', { owner: billing.billing_owner_name })}
                    </p>

                    <ul className="divide-y divide-border">
                        {(billing.billing_groups || []).map((g) => (
                            <Row key={g.member_id || 'shared'} group={g} t={t} />
                        ))}
                    </ul>

                    {mode === 'separate_bills' ? (
                        <div className="space-y-2">
                            <p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                                {t('grocery.billContacts')}
                            </p>
                            {(members || []).map((m) => (
                                <div key={m.id} className="flex flex-wrap items-center gap-2">
                                    <span className="min-w-[88px] text-sm font-semibold">{m.name}</span>
                                    {edit && edit.id === m.id ? (
                                        <>
                                            <Input value={edit.value} autoFocus
                                                onChange={(e) => setEdit({ id: m.id, value: e.target.value })}
                                                data-testid={`grocery-bill-contact-input-${m.id}`}
                                                placeholder={t('grocery.billContactHint')}
                                                className="h-11 flex-1 text-base" />
                                            <button onClick={() => saveContact(m.id, edit.value)}
                                                disabled={busy}
                                                data-testid={`grocery-bill-contact-save-${m.id}`}
                                                className="min-h-[44px] rounded-xl border-2 border-input px-3 text-sm font-semibold">
                                                {t('grocery.save')}
                                            </button>
                                        </>
                                    ) : (
                                        <button onClick={() => may && setEdit({
                                            id: m.id, value: m.bill_email || m.bill_phone || '',
                                        })}
                                            data-testid={`grocery-bill-contact-${m.id}`}
                                            className="flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-emerald-700 dark:text-emerald-300">
                                            <Mail className="h-4 w-4" />
                                            {m.bill_email || m.bill_phone || t('grocery.billAddContact')}
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </section>
    );
};

export default BillingPanel;
